import { useState } from 'react';
import { MAX_MANUAL_TITLE_OWNERS, saveManualTitleOwnersSchema } from '@brighte/validation';
import { PropertyTitleOwnerOutput, useSaveManualTitleOwnersMutation } from '@graphql/index';
import type { ErrorResponse } from '@graphql/index';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Checkbox } from '@spark-web/checkbox';
import { Columns } from '@spark-web/columns';
import { Field } from '@spark-web/field';
import { PlusIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextInput } from '@spark-web/text-input';
import { TextLinkButton } from '@spark-web/text-link';

type Props = {
  applicationId: string;
  owners: PropertyTitleOwnerOutput[];
  onContinue: (owners: PropertyTitleOwnerOutput[]) => void;
  onBack: () => void;
};

type Owner = { id?: string; firstName: string; lastName: string };

const saveErrorMessages = new Map([
  [
    'Title owners cannot be changed after application submission.',
    'This application has already been submitted. Title owners can no longer be changed.',
  ],
  [
    'Wait for document verification to finish before editing title owners.',
    'Wait for document verification to finish before editing title owners.',
  ],
  [
    'Manual declaration is not available for this title search.',
    'Manual entry is no longer available. Go back to check the title search result.',
  ],
]);

export default function DeclareTitleOwners({ applicationId, owners, onContinue, onBack }: Props) {
  const [entries, setEntries] = useState<Owner[]>(() =>
    owners.length
      ? owners.map(owner => {
          const [firstName, ...lastName] = owner.fullName.trim().split(/\s+/);
          return { id: owner.id, firstName, lastName: lastName.join(' ') };
        })
      : [{ firstName: '', lastName: '' }],
  );
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: save, isLoading: saving } = useSaveManualTitleOwnersMutation({ useErrorBoundary: false });
  const input = saveManualTitleOwnersSchema.safeParse({ applicationId, owners: entries, declarationAccepted });

  const changeOwner = (index: number, field: 'firstName' | 'lastName', value: string) => {
    setEntries(previous =>
      previous.map((owner, ownerIndex) => (ownerIndex === index ? { ...owner, [field]: value } : owner)),
    );
    setDeclarationAccepted(false);
    setError(null);
  };

  const submit = async () => {
    if (!input.success || saving) return;
    setError(null);
    try {
      const result = await save({ input: input.data });
      onContinue(result.saveManualTitleOwners.owners as PropertyTitleOwnerOutput[]);
    } catch (saveError) {
      const message = (saveError as ErrorResponse)?.response?.errors?.[0]?.message;
      setError(saveErrorMessages.get(message ?? '') ?? 'We could not save the title owners. Please try again.');
    }
  };

  return (
    <Stack gap="xxlarge">
      <Stack gap="large">
        {entries.map((owner, index) => (
          <Box
            key={owner.id ?? index}
            borderRadius="medium"
            borderWidth="standard"
            border="standard"
            paddingX="large"
            paddingY="xlarge"
          >
            <Stack gap="large">
              <Box display="flex" alignItems="center" justifyContent="spaceBetween">
                <Text size="xsmall" weight="semibold" transform="uppercase">
                  {entries.length > 1 ? `Title owner ${index + 1}` : 'Title owner'}
                </Text>
                {index > 0 && (
                  <Text size="small">
                    <TextLinkButton
                      aria-label={`Remove owner ${index + 1}`}
                      aria-disabled={saving}
                      tone="neutral"
                      weight="semibold"
                      onClick={() => {
                        if (saving) return;
                        setEntries(previous => previous.filter((_, ownerIndex) => ownerIndex !== index));
                        setDeclarationAccepted(false);
                        setError(null);
                      }}
                    >
                      Remove
                    </TextLinkButton>
                  </Text>
                )}
              </Box>
              <Columns gap="large" collapseBelow="tablet">
                <Field label="First name" disabled={saving}>
                  <TextInput
                    placeholder="e.g. Sarah"
                    value={owner.firstName}
                    onChange={event => changeOwner(index, 'firstName', event.target.value)}
                  />
                </Field>
                <Field label="Last name" disabled={saving}>
                  <TextInput
                    placeholder="e.g. Johnson"
                    value={owner.lastName}
                    onChange={event => changeOwner(index, 'lastName', event.target.value)}
                  />
                </Field>
              </Columns>
            </Stack>
          </Box>
        ))}
        <Box display="flex" alignItems="center" gap="large">
          <Button
            prominence="low"
            disabled={saving || entries.length >= MAX_MANUAL_TITLE_OWNERS}
            onClick={() => {
              setEntries(previous => [...previous, { firstName: '', lastName: '' }]);
              setDeclarationAccepted(false);
              setError(null);
            }}
          >
            <PlusIcon size="xsmall" />
            Add another owner
          </Button>
        </Box>
      </Stack>
      <Checkbox
        checked={declarationAccepted}
        disabled={saving}
        onChange={event => setDeclarationAccepted(event.target.checked)}
      >
        I declare that the people named above are the owners listed on the Certificate of title for this property.
      </Checkbox>
      {!input.success && input.error.issues.some(issue => issue.code === 'custom') && (
        <Alert tone="caution">Enter each owner only once.</Alert>
      )}
      {error && <Alert tone="critical">{error}</Alert>}
      <Stack gap="large">
        <Button tone="primary" disabled={!input.success || saving} loading={saving} onClick={submit}>
          Continue
        </Button>
        {/* Back stays here rather than on the parent screen so it shares `saving`: navigating away
            mid-save leaves the mutation in flight, and its onContinue then jumps the customer to
            taxable income from whatever screen they moved to. */}
        <Button prominence="none" tone="neutral" disabled={saving} onClick={onBack}>
          Back
        </Button>
      </Stack>
    </Stack>
  );
}
